// @ts-nocheck
/* ============================================================
   The concrete job after the estimate: the on-site lift log, what the crew
   actually used against what the recipe said, and the warranty terms the job
   carries (plan §5.3–§5.4; roadmap §13 items 11, 12, 19).

   • The LIFT LOG is a list on the work order, `wo.liftLog = [{ id, section,
     sqft?, beforeIn, afterIn, foamLb, holes, note?, at, crewId?, offline? }]`,
     written by the crew through the field-ops outbox (`job.liftlog`,
     lib/outbox.ts ↔ server/src/fieldops.js) so it survives no signal.
   • The ESTIMATE SNAPSHOT `wo.cx = { blocks, slabs, est, warranty }` is stamped
     when the job is created from the quote (app.tsx addProjectFromQuote): a
     crew login has no quotes or rate cards (RLS), and the rate cards change,
     so "what we planned" has to live on the job.
   • RECIPE LEARNING reads the two together: foam per sq ft-inch and crew hours
     per section as they really ran, against the card's components, and the
     component quantities that would make the next estimate match.
   • WARRANTY terms per job type come off the rate cards (years + wording)
     onto the job at creation and are dated at completion.

   Import-free; unit-concrete-actuals.mjs runs it as-is.
   ============================================================ */

export const LIFT_LOG_MAX = 80;
export const LIFT_LIMITS = { inches: 36, foamLb: 5000, holes: 999, sqft: 100000, section: 80, note: 500 };
const num = (v: any): number => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const arr = (v: any): any[] => (Array.isArray(v) ? v : []);
export const liftInches = (e: any): number => Math.max(0, num(e && e.beforeIn) - num(e && e.afterIn));

/* a usable entry, or null — the same rules the service applies (crewops.js) */
export function cleanLiftEntry(e: any) {
  if (!e || typeof e !== "object" || !e.id || !/^[A-Za-z0-9_.:-]{3,80}$/.test(String(e.id))) return null;
  const n = (v: any, max: number, min = 0) => { const x = Number(v); return Number.isFinite(x) ? Math.max(min, Math.min(max, x)) : 0; };
  const out: any = {
    id: String(e.id), section: String(e.section || "").trim().slice(0, LIFT_LIMITS.section) || "Section",
    beforeIn: Math.round(n(e.beforeIn, LIFT_LIMITS.inches) * 100) / 100, afterIn: Math.round(n(e.afterIn, LIFT_LIMITS.inches) * 100) / 100,
    foamLb: Math.round(n(e.foamLb, LIFT_LIMITS.foamLb) * 10) / 10, holes: Math.round(n(e.holes, LIFT_LIMITS.holes)),
  };
  if (Number(e.sqft) > 0) out.sqft = Math.round(n(e.sqft, LIFT_LIMITS.sqft));
  const note = String(e.note || "").trim().slice(0, LIFT_LIMITS.note);
  if (note) out.note = note;
  if (Number(e.at) > 0) out.at = Number(e.at);
  if (e.crewId) out.crewId = String(e.crewId).slice(0, 80);
  return out;
}

/* the sections a crew can log against: the drawn slabs by name, else the lift
   blocks' sections ("Driveway 1", "Driveway 2", …), else nothing (free text) */
export function liftSectionsOf(wo: any): { label: string; sqft: number | null }[] {
  const cx = wo && wo.cx && typeof wo.cx === "object" ? wo.cx : null;
  if (!cx) return [];
  const out: { label: string; sqft: number | null }[] = [];
  const slabs = arr(cx.slabs).filter((s) => s && (s.jobType || "lift") === "lift");
  if (slabs.length) {
    slabs.forEach((s) => {
      const n = Math.max(1, Math.round(num(s.sections)) || 1);
      const base = String(s.label || s.kind || "Slab").split(" · ")[0];
      if (n === 1) out.push({ label: base, sqft: num(s.sqft) || null });
      else for (let i = 1; i <= n; i++) out.push({ label: `${base} ${i}`, sqft: num(s.sqft) ? Math.round(num(s.sqft) / n) : null });
    });
    return out;
  }
  arr(cx.blocks).filter((b) => b && b.jobType === "lift").forEach((b, bi) => {
    const sqft = num(b.sqft);
    const n = Math.max(1, Math.round(num(b.sections)) || Math.ceil(sqft / 100) || 1);
    const kind = arr(b.surfaces)[0];
    const base = kind ? kind.charAt(0).toUpperCase() + kind.slice(1).replace(/-/g, " ") : `Area ${bi + 1}`;
    for (let i = 1; i <= n; i++) out.push({ label: n === 1 ? base : `${base} ${i}`, sqft: sqft ? Math.round(sqft / n) : null });
  });
  return out;
}

/* what the log adds up to */
export function liftTotals(entries: any) {
  const list = arr(entries).filter(Boolean);
  let foamLb = 0, holes = 0, sqftInch = 0, sqftKnown = 0, liftSum = 0;
  list.forEach((e) => {
    foamLb += num(e.foamLb); holes += num(e.holes);
    const li = liftInches(e); liftSum += li;
    if (num(e.sqft) > 0) { sqftKnown += num(e.sqft); sqftInch += num(e.sqft) * li; }
  });
  return { entries: list.length, foamLb: Math.round(foamLb * 10) / 10, holes, sqftInch: Math.round(sqftInch), sqftKnown, avgLiftIn: list.length ? Math.round((liftSum / list.length) * 100) / 100 : 0 };
}

const hoursOf = (wo: any) => arr(wo && wo.timeLogs).reduce((s, e) => s + num(e && e.hours), 0);

/* the job as it ran against the job as it was estimated (wo.cx.est) */
export function liftActuals(wo: any) {
  const t = liftTotals(wo && wo.liftLog);
  const est = wo && wo.cx && wo.cx.est && typeof wo.cx.est === "object" ? wo.cx.est : null;
  // the sq ft-inches the foam went into: the log's own when every section's
  // area is known, else the estimate's (the drop the quote assumed)
  const sqftInch = t.sqftKnown > 0 && t.entries > 0 ? t.sqftInch : num(est && est.sqftInch);
  return { ...t, hours: Math.round(hoursOf(wo) * 100) / 100, sqftInch, est, foamPerSqftInch: sqftInch > 0 && t.foamLb > 0 ? Math.round((t.foamLb / sqftInch) * 1000) / 1000 : null };
}

/* estimated vs actual, row by row, for the job card */
export function liftVariance(wo: any) {
  const a = liftActuals(wo);
  const est = a.est || {};
  const row = (label: string, e: number, act: number, unit: string, digits = 0) => {
    const have = act > 0 || e > 0;
    const pct = e > 0 && act > 0 ? Math.round((act / e - 1) * 100) : null;
    return { label, est: e, actual: act, unit, pct, have, digits };
  };
  return [
    row("Foam", num(est.foamLb), a.foamLb, "lb", 1),
    row("Holes", num(est.holes), a.holes, "", 0),
    row("Crew hours", num(est.hours), a.hours, "hr", 1),
  ].filter((r) => r.have);
}

/* ---- the job's money as it ran (§13 item 28) ----
   The signed quote page's "Job cost — estimated vs actual": the estimate the
   quote figured (its foam in pounds and $/lb, the crew hours at the loaded
   rate, the other materials, the rig, the job's extras, the revenue) next to
   the job as it ran — the lift log's pounds, the clocked hours. What the job
   never measures (the other materials, the rig, the extras) stays at the
   estimate and says so. A lift priced per lb or per cu ft (wo.cx.est.basis)
   has its revenue settled by the log too, never under the minimum. */
const r2 = (n: number) => Math.round(n * 100) / 100;
export function basisBilling(basis: any, actualQty: number | null) {
  if (!basis || !(num(basis.rate) > 0)) return null;
  const estimated = Math.max(num(basis.minimum), r2(num(basis.qty) * num(basis.rate)));
  const actual = actualQty != null && actualQty > 0 ? Math.max(num(basis.minimum), r2(actualQty * num(basis.rate))) : null;
  return { method: basis.method, unit: basis.unit || (basis.method === "lb" ? "lb" : "cu ft"), rate: num(basis.rate), estQty: num(basis.qty), actualQty, estimated, actual, minimum: num(basis.minimum) };
}
export function estimatedVsActual(wo: any, est: any, opts: any = {}) {
  const e = est || {};
  const t = liftTotals(wo && wo.liftLog);
  const logged = t.entries > 0;
  const hours = hoursOf(wo);
  const clocked = hours > 0;
  const crewRate = num(opts.actualCrewRate) > 0 ? num(opts.actualCrewRate) : num(e.crewRate);
  const foamUnit = num(e.foamUnitCost);
  const basis = opts.basis && typeof opts.basis === "object" ? opts.basis : null;
  const actualQty = basis ? (basis.method === "lb" ? (logged ? t.foamLb : null) : (logged && t.sqftKnown > 0 ? r2(t.sqftInch / 12) : null)) : null;
  const billing = basisBilling(basis, actualQty);
  const estRevenue = num(e.revenue);
  const actRevenue = billing && billing.actual != null ? billing.actual : estRevenue;
  const estFoamLb = num(e.foamLb), actFoamLb = logged ? t.foamLb : estFoamLb;
  const estFoamCost = r2(estFoamLb * foamUnit), actFoamCost = r2(actFoamLb * foamUnit);
  const estLabor = r2(num(e.laborHours) * num(e.crewRate)), actHours = clocked ? r2(hours) : num(e.laborHours), actLabor = r2(actHours * crewRate);
  const other = r2(num(e.otherMaterial)), equipment = r2(num(e.equipment)), extras = r2(num(e.extras));
  const estCost = r2(estFoamCost + other + estLabor + equipment + extras);
  const actCost = r2(actFoamCost + other + actLabor + equipment + extras);
  const estProfit = r2(estRevenue - estCost), actProfit = r2(actRevenue - actCost);
  const pctOf = (a: number, b: number) => (b > 0 ? Math.round((a / b - 1) * 100) : null);
  const row = (key: string, label: string, estV: number, actV: number, unit: string, o: any = {}) => ({ key, label, est: estV, actual: actV, unit, money: !!o.money, digits: o.digits == null ? 0 : o.digits, assumed: !!o.assumed, pct: o.assumed ? null : pctOf(actV, estV), note: o.note || "" });
  const rows = [
    row("foamLb", "Foam", estFoamLb, actFoamLb, "lb", { digits: 1, assumed: !logged, note: logged ? `${t.entries} lift log entr${t.entries === 1 ? "y" : "ies"}` : "no lift log yet" }),
    row("foamCost", "Foam cost", estFoamCost, actFoamCost, "$", { money: true, assumed: !logged, note: foamUnit > 0 ? `at $${foamUnit.toFixed(2)}/lb` : "" }),
    row("other", "Other materials", other, other, "$", { money: true, assumed: true, note: "as estimated — the job doesn't measure these" }),
    row("hours", "Crew hours", num(e.laborHours), actHours, "hr", { digits: 1, assumed: !clocked, note: clocked ? "from the crew's clock" : "no hours clocked yet" }),
    row("labor", "Labor", estLabor, actLabor, "$", { money: true, assumed: !clocked, note: crewRate > 0 ? `at $${crewRate.toFixed(2)}/crew-hr` : "" }),
    ...(equipment > 0 ? [row("equipment", "Equipment", equipment, equipment, "$", { money: true, assumed: true, note: "as estimated" })] : []),
    ...(extras > 0 ? [row("extras", "Travel, overhead, tax, shipping", extras, extras, "$", { money: true, assumed: true, note: "as estimated" })] : []),
    row("cost", "Total cost", estCost, actCost, "$", { money: true, assumed: !logged && !clocked }),
    row("revenue", billing ? `Price · by the ${billing.unit === "lb" ? "pound" : "cubic foot"}` : "Price", estRevenue, actRevenue, "$", { money: true, assumed: !(billing && billing.actual != null), note: billing ? (billing.actual != null ? `${billing.actualQty} ${billing.unit} × $${billing.rate.toFixed(2)}${billing.minimum > 0 && billing.actual === billing.minimum ? " · the minimum" : ""}` : `${billing.estQty} ${billing.unit} × $${billing.rate.toFixed(2)} estimated — final by the lift log`) : "the signed price" }),
    row("profit", "Gross profit", estProfit, actProfit, "$", { money: true, assumed: !logged && !clocked }),
  ];
  const margin = (p: number, rev: number) => (rev > 0 ? r2((p / rev) * 100) : 0);
  const drawn = wo && wo.stockDrawn && typeof wo.stockDrawn === "object" ? { sets: num(wo.stockDrawn.sets), item: String(wo.stockDrawn.item || ""), at: num(wo.stockDrawnAt) || null } : null;
  return { rows, logged, clocked, billing, drawn,
    est: { revenue: estRevenue, cost: estCost, profit: estProfit, marginPct: margin(estProfit, estRevenue), foamLb: estFoamLb, hours: num(e.laborHours) },
    actual: { revenue: actRevenue, cost: actCost, profit: actProfit, marginPct: margin(actProfit, actRevenue), foamLb: actFoamLb, hours: actHours } };
}

/* ---- recipe learning ----
   Across the finished lift jobs that have a log and clocked hours: foam per
   sq ft-inch and crew hours per section as they really ran, next to the card's
   components, and the quantities that would make the recipe match. The foam
   suggestion backs the component's waste allowance out (the takeoff adds it
   again); the hours-per-section suggestion takes the card's other labor lines
   (per visit, per hole) off the clocked total first. Needs `minJobs` jobs
   before it says anything. */
export function suggestRecipe(workOrders: any, card: any, opts: any = {}) {
  const minJobs = Number(opts.minJobs) > 0 ? Number(opts.minJobs) : 3;
  const comps = arr(card && card.components);
  const foamComp = comps.find((c) => c && c.kind === "material" && c.per === "sqft-inch") || null;
  const sectionComp = comps.find((c) => c && c.kind === "labor" && c.per === "section") || null;
  const visitHours = comps.filter((c) => c && c.kind === "labor" && c.per === "visit").reduce((s, c) => s + num(c.qty), 0);
  const holeHours = comps.filter((c) => c && c.kind === "labor" && c.per === "hole").reduce((s, c) => s + num(c.qty), 0);
  const jobs = arr(workOrders).filter((wo) => wo && /^(done|completed)$/i.test(String(wo.status || "")) && arr(wo.liftLog).length > 0);
  let foamLb = 0, sqftInch = 0, hours = 0, sections = 0, holes = 0, foamJobs = 0, hourJobs = 0;
  jobs.forEach((wo) => {
    const a = liftActuals(wo);
    if (a.foamLb > 0 && a.sqftInch > 0) { foamLb += a.foamLb; sqftInch += a.sqftInch; foamJobs++; }
    if (a.hours > 0 && a.entries > 0) { hours += a.hours; sections += a.entries; holes += a.holes; hourJobs++; }
  });
  const waste = num(foamComp && foamComp.waste);
  const foamActual = sqftInch > 0 ? foamLb / sqftInch : null;
  const foamSuggested = foamActual != null ? Math.round((foamActual / (1 + waste / 100)) * 1000) / 1000 : null;
  const secActual = sections > 0 ? Math.max(0, hours - visitHours * hourJobs - holeHours * holes) / sections : null;
  const secSuggested = secActual != null ? Math.round(secActual * 100) / 100 : null;
  const pct = (cur: number, next: number | null) => (next != null && cur > 0 ? Math.round((next / cur - 1) * 100) : null);
  const out: any = {
    jobs: jobs.length, enough: foamJobs >= minJobs || hourJobs >= minJobs, minJobs,
    foam: foamComp ? { jobs: foamJobs, actualPerSqftInch: foamActual != null ? Math.round(foamActual * 1000) / 1000 : null, current: num(foamComp.qty), waste, suggested: foamSuggested, pct: pct(num(foamComp.qty), foamSuggested), desc: foamComp.desc || "Lifting foam", unit: foamComp.unit || "lb" } : null,
    hours: sectionComp ? { jobs: hourJobs, actualPerSection: secSuggested, current: num(sectionComp.qty), suggested: secSuggested, pct: pct(num(sectionComp.qty), secSuggested), desc: sectionComp.desc || "Drill, inject & lift" } : null,
  };
  // the card's components with the suggestions applied (the editor's Save writes them)
  const differs = (a: number, b: number | null) => b != null && Math.abs(a - b) > 1e-6;
  out.components = comps.map((c) => {
    if (foamComp && c === foamComp && foamJobs >= minJobs && differs(num(c.qty), foamSuggested)) return { ...c, qty: foamSuggested };
    if (sectionComp && c === sectionComp && hourJobs >= minJobs && differs(num(c.qty), secSuggested)) return { ...c, qty: secSuggested };
    return c;
  });
  out.changed = out.components.some((c, i) => c !== comps[i]);
  return out;
}

/* ---- margin by job type / by drop (the reports) ----
   rows: [{ jobType, drop?, amount, cost, profit, hours? }] from the priced
   blocks of won quotes (reports.tsx zips computeQuoteView's blocks with the
   quote's inputs for the drop) */
export function groupMargin(rows: any, key: "jobType" | "drop") {
  const by: Record<string, any> = {};
  arr(rows).forEach((r) => {
    if (!r) return;
    const k = String(r[key] || (key === "drop" ? "unsure" : "other"));
    const g = by[k] || (by[k] = { key: k, jobs: 0, revenue: 0, cost: 0, profit: 0, hours: 0 });
    g.jobs++; g.revenue += num(r.amount); g.cost += num(r.cost); g.profit += num(r.profit); g.hours += num(r.hours);
  });
  return Object.values(by).map((g: any) => ({ ...g, pct: g.revenue > 0 ? Math.round((g.profit / g.revenue) * 100) : 0 })).sort((a: any, b: any) => b.revenue - a.revenue);
}

/* ---- warranty ---- */
export function warrantyTermsFor(jobTypes: any, cards: any) {
  const out: { jobType: string; years: number | null; text: string }[] = [];
  arr(jobTypes).forEach((jt) => {
    const c = cards && cards[jt];
    if (!c || typeof c !== "object") return;
    const years = Number(c.warrantyYears) > 0 ? Number(c.warrantyYears) : null;
    const text = typeof c.warranty === "string" ? c.warranty.trim() : "";
    if (years || text) out.push({ jobType: jt, years, text });
  });
  return out;
}
/* the day a term runs out, from the completion time (UTC date, the same on the phone and the service) */
export const warrantyUntil = (completedAt: any, years: any): string | null => {
  const ms = Number(completedAt), y = Number(years);
  if (!(ms > 0) || !(y > 0)) return null;
  return new Date(ms + y * 365.25 * 86400000).toISOString().slice(0, 10);
};
/* the terms dated at completion — what job.complete stamps on the job */
export function datedWarranty(terms: any, completedAt: any) {
  return arr(terms).filter((t) => t && t.jobType).map((t) => ({ jobType: t.jobType, years: Number(t.years) > 0 ? Number(t.years) : null, text: String(t.text || ""), until: warrantyUntil(completedAt, t.years) }));
}
export const JOB_SHORT: Record<string, string> = { lift: "Lifting & leveling", coat: "Floor coating", clean: "Cleaning", seal: "Sealing", caulk: "Caulking" };
export function warrantyLine(t: any): string {
  if (!t) return "";
  const parts = [JOB_SHORT[t.jobType] || t.jobType];
  if (t.years) parts.push(`${t.years} year${t.years === 1 ? "" : "s"}${t.until ? ` · through ${t.until}` : ""}`);
  if (t.text) parts.push(t.text);
  return parts.join(" · ");
}
